import { useEffect } from 'react';
import type { JackpotProduct } from '../../@types';
import { useMarketplaceConfig } from '../../queries/marketplace';
import { PRODUCT } from '../../types/enums';
import useWinningJackpot from '../useWinningJackpot';
import useProductAvailability from '../useProductAvailability';

export const JACKPOT_WINNER_DISMISS_MS = 10_000;

const useIsJackpotEnabled = (product: JackpotProduct) => {
  const { data: config } = useMarketplaceConfig();
  const { canViewCasinoContent } = useProductAvailability();

  // Sportsbook jackpots have no marketplace flag; they are always on.
  if (product === PRODUCT.SPORTSBOOK) return true;

  return canViewCasinoContent && (config?.casinoConfig?.jackpotEnabled ?? false);
};

/**
 * Drives one product's jackpot win celebration: subscribes to that product's wins, auto-dismisses after
 * JACKPOT_WINNER_DISMISS_MS, and leaves fullscreen games so the celebration is seen.
 */
export const useJackpotWinnerOverlay = (product: JackpotProduct) => {
  const isEnabled = useIsJackpotEnabled(product);
  const { clearJackpot, winningJackpot } = useWinningJackpot(product, isEnabled);
  const activeJackpot = isEnabled ? winningJackpot : null;

  useEffect(() => {
    if (!activeJackpot) return;

    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
    }

    const dismissTimer = window.setTimeout(clearJackpot, JACKPOT_WINNER_DISMISS_MS);

    return () => window.clearTimeout(dismissTimer);
  }, [activeJackpot, clearJackpot]);

  return { activeJackpot, clearJackpot };
};
